import * as moment from "moment";
import mergeConfig from "./mergeConfig";

const initializeSettings = (widget, config) => {
  /**
   * Main configuration settings
   *
   * All settings can be set via `data-*` or passed in as a config object
   * @typedef {Object} Settings
   * @property {string} [mode=list]
   * Sets display mode of widget<br/>
   * `"list"` - displays list of N number of posts specified by itemsPerPage<br/>
   * `"item"` - displays the news item based on slug provided in the url
   *
   * @property {boolean} [dev=false]
   * Turns on DEV mode which uses the staging DCIS API
   *
   * @property {string} [lang=en]
   * Sets the language of the widget - language is used for retrieving translated DCIS content, labels and date format<br/>
   * `"en"` | `"fr"` | `"es"`
   *
   * @property {string} [className]
   * Adds a class to the main wrapper div
   *
   * @property {string|boolean} [showDate=false]
   * Determine whether and where to display the publish date<br/>
   * `true` - Displays date before post title<br/>
   * `"top"` - Displays date above image and header<br/>
   * `"below"` - Displays date after the header<br/>
   * `false` - Hides the date
   *
   * @property {boolean} [showImage=false]
   * Determine whether to display the article featured image or not
   *
   * @property {number} [itermsPerPage=5]
   * Sets the number of news item shown at a time	- also used to determine how many items to retreive from DCIS
   *
   * @property {string[]} [brand=["Sunwing Travel Group"]]
   * Array list of brands to retreive from DCIS - All single quotes are replaced with double quotes to allow it to be parsed as an array
   *
   * @property {string} [dateStart=1990-01-01]
   * Sets the earliest article's published date to retreive from DCIS<br/>
   * Format - ("YYYY-MM-DD")
   *
   * @property {string} [dateEnd=2100-12-31]
   * Sets the latest article's published date to retreive from DCIS<br/>
   * Format - ("YYYY-MM-DD")
   *
   * @property {string} [basePath=/]
   * Sets the path to be prepended to the slug for the news-post widget for displaying the articles when `useModal` = `false`
   *
   * @property {string} [fallbackImage=Contentful Image]
   * Sets the fallback featured image when no featured image is supplied && `showImage` = `true`<br/>
   * `?w=720&h=405&fit=thumb` is appended to URL to retrieve scaled image from Contentful Image API<br/>
   * [Default Contentful Image Placeholder](https://images.contentful.com/h82kzjd39wa1/52dU9EDRHyg2ymSUW8KMaq/fe37c6e839d7cedb5561f1fc8fa85b6a/001.jpg)
   *
   * @property {boolean} [useImageLink=false]
   * Determine whether to link the article featured image or not
   *
   * @property {boolean} [showHeader=false]
   * Determine whether to display a header title above the list view or not
   *
   * @property {string} [headerTitle]
   * Allows for custom text override of title displayed above the list view
   *
   * @property {string} [noNewsMessage]
   * Allows for custom text override of message in the list view when no news items are available
   *
   * @property {boolean} [showExcerpt=false]
   * Determine whether to display the article excerpt or not
   *
   * @property {boolean} [showReadMore=true]
   * Determine whether to display the Read More button or not - true will deactivate the link from the title
   *
   * @property {boolean} [usePagination=true]
   * Determine whether to display the pagination or not
   *
   * @property {number} [paginationOffset=1]
   * Sets how many items to either side of the current page in the pagination<br/>
   * ie : `paginationOffset` = `1` on page 9 would become [1] [.] [8] [9] [10] [.] [15]<br/>
   * ie : `paginationOffset` = `2` on page 9 would become [1] [.] [7] [8] [9] [10] [11] [.] [15]
   *
   * @property {string} [backdropColour=rgba(255,255,255,0.8)]
   * Sets the widget's modal backdrop overlay colour
   * Format - any valid css color (rgb, rgba, hsl, hsla, hex)
   *
   * @property {boolean} [useModal=false]
   * Determine whether to display news in modal - false will use a link generated by the basePath + slug of article
   *
   * @property {boolean} [showModalImage=true]
   * Determine whether to display the article featured image in the modal or not
   *
   * @property {boolean} [showModalExcerpt=false]
   * Determine whether to display the article excerpt in the modal or not
   *
   * @property {string} [redirectErrorUrl=/]
   * Url to redirect to after failing to retreive news item
   *
   * @property {string} [slug]
   * Alternate way to specify which item to display - overrides slug from URL
   */

  // =====================================================================
  // Common Configuration
  // =====================================================================
  let mode = mergeConfig(widget, config, "mode", "list");
  let dev = mergeConfig(widget, config, "dev", false, "boolean");
  let lang = mergeConfig(widget, config, "lang", "en");
  let customClassName = mergeConfig(widget, config, "customClassName", "");
  let showDate = mergeConfig(
    widget,
    config,
    "showDate",
    false,
    "string-boolean"
  );
  let showImage = mergeConfig(widget, config, "showImage", false, "boolean");
  let itemsPerPage = mergeConfig(widget, config, "itemsPerPage", 5, "int");

  // =====================================================================
  // Query Parameters
  // =====================================================================
  let brand = mergeConfig(
    widget,
    config,
    "brand",
    ["Sunwing Travel Group"],
    "json"
  );
  let dateStart = mergeConfig(widget, config, "dateStart", "1990-01-01");
  let dateEnd = mergeConfig(widget, config, "dateEnd", "2100-12-31");

  // =====================================================================
  // List View Configuration
  // =====================================================================
  let basePath = mergeConfig(widget, config, "basePath", "/");
  let fallbackImage = mergeConfig(
    widget,
    config,
    "fallbackImage",
    "https://images.contentful.com/h82kzjd39wa1/52dU9EDRHyg2ymSUW8KMaq/fe37c6e839d7cedb5561f1fc8fa85b6a/001.jpg"
  );
  let useImageLink = mergeConfig(
    widget,
    config,
    "useImageLink",
    false,
    "boolean"
  );
  let showHeader = mergeConfig(widget, config, "showHeader", false, "boolean");
  let headerTitle = mergeConfig(widget, config, "headerTitle", "");
  let noNewsMessage = mergeConfig(widget, config, "noNewsMessage", "");
  let showExcerpt = mergeConfig(
    widget,
    config,
    "showExcerpt",
    false,
    "boolean"
  );
  let showReadMore = mergeConfig(
    widget,
    config,
    "showReadMore",
    true,
    "boolean"
  );

  // =====================================================================
  // Pagination Configuration - Applicable only when in list view mode
  // =====================================================================
  let usePagination = mergeConfig(
    widget,
    config,
    "usePagination",
    true,
    "boolean"
  );
  let paginationOffset = mergeConfig(
    widget,
    config,
    "paginationOffset",
    1,
    "int"
  );

  // =====================================================================
  // Modal Configuration - Applicable only when in list view mode
  // =====================================================================
  let backdropColour = mergeConfig(
    widget,
    config,
    "backdropColour",
    "rgba(255,255,255,0.8)"
  );
  let useModal = mergeConfig(widget, config, "usemodal", false, "boolean");
  let showModalImage = mergeConfig(
    widget,
    config,
    "showModalImage",
    true,
    "boolean"
  );
  let showModalExcerpt = mergeConfig(
    widget,
    config,
    "showModalExcerpt",
    false,
    "boolean"
  );

  // =====================================================================
  // Item View Configuration
  // =====================================================================
  let redirectErrorUrl = mergeConfig(widget, config, "redirectErrorUrl", "/");
  let slug = mergeConfig(widget, config, "slug", "");

  if (slug === "") {
    let path = location.pathname.split("/");
    let _slug = path[path.length - 1];

    if (_slug === "") {
      slug = redirectErrorUrl;
    } else {
      slug = _slug;
    }
  }

  // =====================================================================
  // Initialize Moment.js locale
  // =====================================================================
  moment.locale(lang);

  return {
    // Common
    mode: mode,
    customClassName: customClassName,
    showDate: showDate,
    showImage: showImage,
    showExcerpt: showExcerpt,
    // Query Parameters
    brand: brand,
    dateStart: dateStart,
    dateEnd: dateEnd,
    dev: dev,
    lang: lang,
    // Modal Settings
    backdropColour: backdropColour,
    showModalImage: showModalImage,
    showModalExcerpt: showModalExcerpt,
    // List View
    basePath: basePath,
    fallbackImage: fallbackImage,
    itemsPerPage: itemsPerPage,
    paginationOffset: paginationOffset,
    showHeader: showHeader,
    headerTitle: headerTitle,
    showReadMore: showReadMore,
    useImageLink: useImageLink,
    useModal: useModal,
    usePagination: usePagination,
    // Item View
    slug: slug,
    redirectErrorUrl: redirectErrorUrl
  };
};

export default initializeSettings;
